<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { toTypedSchema } from '@/lib/zodTypedSchema'
import * as z from 'zod'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toast } from '@/components/ui/toast'
import { API, HOST } from '@/utils/http'
import { Loader2 } from 'lucide-vue-next'
import type { AdminUser } from '@/types/admin'

const props = defineProps<{
  user: AdminUser
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'user-updated'): void
}>()

const loading = ref(false)

const formSchema = toTypedSchema(z.object({
  display_name: z.string().min(1, 'Display name is required').max(255),
  first_name: z.string().optional().nullable(),
  last_name: z.string().optional().nullable(),
  email: z.string().email('Invalid email address').optional().nullable(),
  phone: z.string().optional().nullable(),
}))

const initialValues = computed(() => ({
  display_name: props.user.display_name || '',
  first_name: props.user.first_name || '',
  last_name: props.user.last_name || '',
  email: props.user.email || '',
  phone: props.user.phone || '',
}))

const onSubmit = async (values: any) => {
  loading.value = true
  try {
    const payload = {
      display_name: values.display_name || null,
      first_name: values.first_name || null,
      last_name: values.last_name || null,
      email: values.email || null,
      phone: values.phone || null,
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/admin/users/${props.user.member_id}`,
      data: payload
    })

    toast({
      title: 'User updated successfully',
    })

    emit('user-updated')
    emit('update:open', false)
  } catch (error: any) {
    const errorMessage = error.response?.data?.detail || error.message || 'Failed to update user'
    toast({
      title: errorMessage,
      variant: 'destructive'
    })
  } finally {
    loading.value = false
  }
}

const close = () => {
  emit('update:open', false)
}

watch(() => props.open, (newVal) => {
  if (!newVal) {
    loading.value = false
  }
})
</script>

<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="max-w-md">
      <DialogHeader>
        <DialogTitle>
          Edit Member
          <Badge v-if="user.is_super_user" variant="secondary" class="ml-2">
            Super Admin
          </Badge>
        </DialogTitle>
        <DialogDescription>
           Member ID: {{ user.member_id }}
        </DialogDescription>
      </DialogHeader>

      <Form
        class="space-y-4"
        :validation-schema="formSchema"
        :initial-values="initialValues"
        @submit="onSubmit"
      >
        <FormField v-slot="{ componentField }" name="display_name">
          <FormItem>
            <FormLabel>Display Name (Legacy)</FormLabel>
            <FormControl>
              <Input v-bind="componentField" placeholder="Enter display name" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <div class="grid grid-cols-2 gap-4">
          <FormField v-slot="{ componentField }" name="first_name">
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" placeholder="Enter first name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField v-slot="{ componentField }" name="last_name">
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" placeholder="Enter last name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>
        </div>

        <FormField v-slot="{ componentField }" name="email">
          <FormItem>
            <FormLabel>Email</FormLabel>
            <FormControl>
              <Input
                v-bind="componentField"
                type="email"
                placeholder="user@example.com"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <FormField v-slot="{ componentField }" name="phone">
          <FormItem>
            <FormLabel>Phone</FormLabel>
            <FormControl>
              <Input v-bind="componentField" placeholder="Enter phone number" />
            </FormControl>
            <FormMessage />
          </FormItem>
        </FormField>

        <DialogFooter>
          <Button
            variant="ghost"
            type="button"
            @click="close"
            :disabled="loading"
          >
            Cancel
          </Button>
          <Button type="submit" :disabled="loading">
            <Loader2 v-if="loading" class="mr-2 h-4 w-4 animate-spin" />
            {{ loading ? 'Updating...' : 'Update' }}
          </Button>
        </DialogFooter>
      </Form>
    </DialogContent>
  </Dialog>
</template>
